/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useState } from "react";
import ct from "class-types.macro";
import { z } from "zod";
import { SubmitHandler, UseFormReturn } from "react-hook-form";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import Form from "~/common/Form";
import Input from "~/common/Form/Input";
import DryerOutputSection from "./DryerOutputSection";

const schema = z.object({
  casesUsed: z.coerce
    .number({
      invalid_type_error:
        "How many paper towel cases do you use annually? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  sheetsPerCase: z.coerce
    .number({
      invalid_type_error:
        "How many paper towel sheets are included in each case you purchase? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  costPerCase: z.coerce
    .number({
      invalid_type_error:
        "What is the cost of each case you purchase? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  costOfElectricity: z.coerce
    .number({
      invalid_type_error:
        "What do you pay for electricity (per kWh)? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  handDryerKilowatts: z.coerce
    .number({
      invalid_type_error:
        "What is the wattage of the hand dryer you're interested in (in kW)? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  maxHandDries: z.coerce
    .number({
      invalid_type_error:
        "What is the max number of hand dries per hour for the hand dryer you're interested in? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  costOfEachDryer: z.coerce
    .number({
      invalid_type_error:
        "What is the cost of the hand dryer you're interested in? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  installationCost: z.coerce
    .number({
      invalid_type_error:
        "What would your electrician charge you to install one hand dryer? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
  handDryersPurchased: z.coerce
    .number({
      invalid_type_error:
        "How many hand dryers are you interested in purchasing? Must be a number.",
    })
    .positive({ message: "Please enter a positive number" }),
});

interface FormFields {
  casesUsed: string;
  sheetsPerCase: string;
  costPerCase: string;
  costOfElectricity: string;
  handDryerKilowatts: string;
  maxHandDries: string;
  costOfEachDryer: string;
  installationCost: string;
  handDryersPurchased: string;
}

const costOfElectricityDefault = "0.10";
const handDryerKilowattsDefault = "1.50";
const maxHandDriesDefault = "240";
const installationCostDefault = "200";

const HandDryerCalculator = () => {
  const [annualPaperTowelCost, setAnnualPaperTowelCost] = useState("");
  const [annualHandlingCost, setAnnualHandlingCost] = useState("");
  const [
    annualCostOfPaperTowelUsage,
    setannualCostOfPaperTowelUsage,
  ] = useState("");

  const [
    annualHoursOfHandDryerUsage,
    setAnnualHoursOfHandDryerUsage,
  ] = useState("");
  const [
    annualhandDryerOperatingCost,
    setAnnualhandDryerOperatingCost,
  ] = useState("");
  const [yourAnnualSavings, setYourAnnualSavings] = useState("");

  const [
    totalHandDryerPurchasePrice,
    setTotalHandDryerPurchasePrice,
  ] = useState("");
  const [monthsUntilPayback, setMonthsUntilPayback] = useState("");

  const formRef = useRef<UseFormReturn<FormFields>>();

  const handleReset = () => {
    setAnnualPaperTowelCost("");
    setAnnualHandlingCost("");
    setannualCostOfPaperTowelUsage("");
    setAnnualHoursOfHandDryerUsage("");
    setAnnualhandDryerOperatingCost("");
    setYourAnnualSavings("");
    setTotalHandDryerPurchasePrice("");
    setMonthsUntilPayback("");

    formRef.current?.reset({
      casesUsed: "",
      sheetsPerCase: "",
      costPerCase: "",
      costOfElectricity: costOfElectricityDefault,
      handDryerKilowatts: handDryerKilowattsDefault,
      maxHandDries: maxHandDriesDefault,
      costOfEachDryer: "",
      installationCost: installationCostDefault,
      handDryersPurchased: "",
    });

    topRef?.current?.scrollIntoView();
  };

  const topRef = useRef<HTMLDivElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  const handleSubmit: SubmitHandler<FormFields> = (data) => {
    const casesUsedNumber = Number(data.casesUsed);
    const sheetsPerCaseNumber = Number(data.sheetsPerCase);
    const costPerCaseNumber = Number(data.costPerCase);

    const annualPaperTowelCost = costPerCaseNumber * casesUsedNumber;
    const handlingCost = annualPaperTowelCost / 2;
    const annualUsageCost = annualPaperTowelCost + handlingCost;
    setAnnualPaperTowelCost(
      `${formatAsCurrency.formatIfUndefined(annualPaperTowelCost)}`
    );
    setAnnualHandlingCost(
      `${formatAsCurrency.formatIfUndefined(handlingCost)}`
    );
    setannualCostOfPaperTowelUsage(
      `${formatAsCurrency.formatIfUndefined(annualUsageCost)}`
    );

    const costOfElectricityNumber = Number(data.costOfElectricity);
    const handDryerKilowattsNumber = Number(data.handDryerKilowatts);
    const maxHandDriesNumber = Number(data.maxHandDries);

    const hoursOfUsage =
      (casesUsedNumber * sheetsPerCaseNumber) / 2.5 / maxHandDriesNumber;
    const operatingCost =
      hoursOfUsage * costOfElectricityNumber * handDryerKilowattsNumber;
    const savings = annualUsageCost - operatingCost;
    setAnnualHoursOfHandDryerUsage(
      hoursOfUsage.toLocaleString("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      })
    );
    setAnnualhandDryerOperatingCost(
      `${formatAsCurrency.formatIfUndefined(operatingCost)}`
    );
    setYourAnnualSavings(`${formatAsCurrency.formatIfUndefined(savings)}`);

    const costOfDryerEachNumber = Number(data.costOfEachDryer);
    const installationCostNumber = Number(data.installationCost);
    const handDryersPurchasedNumber = Number(data.handDryersPurchased);

    const totalPurchasePrice =
      (costOfDryerEachNumber + installationCostNumber) *
      handDryersPurchasedNumber;

    setTotalHandDryerPurchasePrice(
      `${formatAsCurrency.formatIfUndefined(totalPurchasePrice)}`
    );
    setMonthsUntilPayback(
      ((totalPurchasePrice / savings) * 12).toLocaleString("en", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      })
    );

    outputRef?.current?.scrollIntoView();
  };

  const formStyle = ct(
    "bg-gray-200",
    "border-gray-300",
    "border-solid",
    "border",
    "leading-6",
    "mx-auto",
    "mt-5",
    "mb-10",
    "text-left",
    "xxl:w-11/12"
  );

  const submitStyle = ct(
    "border-gray-200",
    "border-solid",
    "border",
    "border-r-0",
    "border-b-0",
    "border-l-0",
    "border-t",
    "px-5",
    "py-2-1/2"
  );

  return (
    <div
      style={{
        borderRadius: "4px",
        boxShadow: "0 1px 4px rgb(0 0 0 / 8%), 0 1px 0 white inset",
      }}
      className={formStyle}
    >
      <Form<FormFields>
        validationSchema={schema}
        onSubmit={handleSubmit}
        shouldFocusError
        validationMode="onSubmit"
        formMethodsRef={formRef}
      >
        <div
          ref={topRef}
          style={{ textShadow: "0 1px 0 white" }}
          className={ct("text-xl", "py-5", "px-5")}
        >
          <div data-testid="input-container">
            <Input
              name="casesUsed"
              label="Paper Towel Cases Used Annually"
              placeholder="Enter number of paper towel cases used annually."
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="cases-used-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="sheetsPerCase"
              label="Paper Towel Sheets Per Case"
              placeholder="Enter number of paper towel sheets in each case."
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="sheets-per-case-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="costPerCase"
              label="Cost Per Paper Towel Case"
              placeholder="Enter cost of the paper towel case."
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="cost-per-case-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="costOfElectricity"
              label="Cost of Electricity Per kWh"
              placeholder="Enter your per kWh cost of electricity."
              defaultValues={costOfElectricityDefault}
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="cost-of-electricity-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="handDryerKilowatts"
              label="Hand Dryer Kilowatts"
              placeholder="Enter the hand dryer kilowatts"
              defaultValues={handDryerKilowattsDefault}
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="hand-dryer-kilowatts-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="maxHandDries"
              label="Max Hand Dries Per Hour"
              placeholder="Enter the hand dryer's maximum hand dries per hour."
              defaultValues={maxHandDriesDefault}
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="max-hand-dries-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="costOfEachDryer"
              label="Cost of Each Hand Dryer"
              placeholder="Enter cost of the hand dryer."
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="cost-of-each-dryer-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="installationCost"
              label="Installation Cost of Each Hand Dryer"
              placeholder="Enter the installation cost of the hand dryer."
              defaultValues={installationCostDefault}
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="installation-cost-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
            <Input
              name="handDryersPurchased"
              label="Number of Hand Dryers Purchased"
              placeholder="Enter number of hand dryers you'll be purchasing."
              labelClassName={ct("font-semibold", "mb-2", "text-gray-800")}
              data-testid="hand-dryers-purchased-input"
              clearErrorOnFocus={false}
              overrideDefaultErrorPositioning
              errorPosition="relative"
              errorClassName="leading-none block mt-1"
            />
          </div>
          <hr className={ct("mt-0", "mb-0")} />
          <div ref={outputRef} data-testid="output-container">
            <DryerOutputSection
              label="Annual Paper Towel Cost"
              value={annualPaperTowelCost}
              dataTestId="annual-paper-towel-cost-output"
            />
            <DryerOutputSection
              label="Annual Handling Cost"
              value={annualHandlingCost}
              dataTestId="annual-handling-cost-output"
            />
            <DryerOutputSection
              label="Annual Cost of Paper Towel Usage"
              value={annualCostOfPaperTowelUsage}
              dataTestId="annual-cost-of-paper-towel-usage-output"
            />
            <DryerOutputSection
              label="Annual Hours of Hand Dryer Usage"
              value={annualHoursOfHandDryerUsage}
              dataTestId="annual-hours-of-hand-dryer-usage-output"
            />
            <DryerOutputSection
              label="Annual Hand Dryer Operating Cost"
              value={annualhandDryerOperatingCost}
              dataTestId="annual-hand-dryer-operating-cost-output"
            />
            <DryerOutputSection
              label="Your Annual Savings"
              value={yourAnnualSavings}
              dataTestId="your-annual-savings-output"
            />
            <DryerOutputSection
              label="Total Hand Dryer Purchase Price"
              value={totalHandDryerPurchasePrice}
              dataTestId="total-hand-dryer-purchas-price-output"
            />
            <DryerOutputSection
              label="Months Until Payback"
              value={monthsUntilPayback}
              dataTestId="months-until-payback-output"
            />
          </div>
        </div>
        <div className={submitStyle}>
          <button
            type="submit"
            className="btn btn-primary"
            data-testid="dryer-calculate-button"
          >
            Calculate
          </button>
          <input
            className={ct("float-right", "text-base", "py-2")}
            type="reset"
            value="Reset"
            onClick={() => handleReset()}
            data-testid="dryer-reset-button"
          />
        </div>
      </Form>
    </div>
  );
};

export default HandDryerCalculator;
